/**
 * useNewRecipes — drives the "Nye opskrifter siden sidste besøg" feature.
 *
 * Single source of truth for:
 *   • The nav badge ("3 nye" pill next to Opskrifter)
 *   • The counter at the top of /recipes
 *   • The "Vis kun nye"-filter on /recipes
 *   • The "Ny"-mærke on each RecipeCard
 *
 * Mental model
 * ────────────
 * `lastSeenAt` (in localStorage) is the watermark of the user's previous
 * visit. The badge polls /api/recipes/new-count?since=lastSeenAt and shows
 * the answer in the nav. When the user actually opens /recipes, we FREEZE
 * a session-local copy of `lastSeenAt` (so "Ny"-mærker stay stable while
 * they're browsing), then update the localStorage watermark to "now" on
 * unmount — so the next visit starts a fresh delta.
 *
 * Works equally for guests and authenticated users (purely client-side
 * watermark — the server endpoint just answers "what's new since X" and
 * doesn't care who's asking). For first-time visitors we seed the watermark
 * on first render so they don't see the entire catalog flagged "Ny".
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../App';

const STORAGE_KEY = 'slushbook_last_seen_recipes_at';
const POLL_MS = 60_000;

const readStored = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStored = (iso) => {
  try {
    localStorage.setItem(STORAGE_KEY, iso);
  } catch {
    /* localStorage disabled / private mode — feature degrades gracefully */
  }
};

/**
 * Hook for the nav badge. Polls every 60s and after window focus.
 *
 * Returns `{ newCount, lastSeenAt, refresh, markAllSeen }`.
 */
export const useNewRecipesCount = () => {
  const [newCount, setNewCount] = useState(0);
  const [lastSeenAt, setLastSeenAt] = useState(() => readStored());

  // Seed watermark on first ever visit so we don't flag the entire catalog.
  useEffect(() => {
    if (!lastSeenAt) {
      const now = new Date().toISOString();
      writeStored(now);
      setLastSeenAt(now);
    }
  }, [lastSeenAt]);

  const refresh = useCallback(async () => {
    const since = readStored();
    if (!since) {
      setNewCount(0);
      return;
    }
    try {
      const res = await axios.get(`${API}/recipes/new-count`, {
        params: { since },
      });
      setNewCount(res.data?.count || 0);
    } catch {
      // Soft-fail — keep last known value rather than flashing 0.
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  const markAllSeen = useCallback(() => {
    const now = new Date().toISOString();
    writeStored(now);
    setLastSeenAt(now);
    setNewCount(0);
  }, []);

  return { newCount, lastSeenAt, refresh, markAllSeen };
};

/**
 * Hook for the Recipes page itself. Freezes the watermark for the duration
 * of the visit so "Ny"-mærker on cards stay stable, then bumps the
 * watermark to "now" on unmount so the next visit's delta is fresh.
 *
 * Returns `{ frozenSince, isNew, newCount }` where:
 *   • frozenSince  — ISO of the previous visit (for display: "siden 14. feb")
 *   • isNew(recipe) — predicate the RecipeCard uses to show the "Ny" pill
 *   • newCount     — number of recipes in `recipes` that are new
 */
export const useFrozenNewRecipes = (recipes) => {
  // Capture the watermark ONCE on mount. We can't read it lazily because
  // we update localStorage on unmount and re-renders would otherwise see
  // the new value and hide all "Ny" badges mid-visit.
  const frozenRef = useRef(null);
  if (frozenRef.current === null) {
    frozenRef.current = readStored() || new Date().toISOString();
  }
  const frozenSince = frozenRef.current;

  // Bump the watermark to "now" on unmount so next visit starts a fresh
  // delta. We use the ref so cleanup doesn't capture a stale value.
  useEffect(() => {
    return () => {
      writeStored(new Date().toISOString());
    };
  }, []);

  const isNew = useCallback(
    (recipe) => {
      const created = recipe?.created_at;
      if (!created || !frozenSince) return false;
      return new Date(created) > new Date(frozenSince);
    },
    [frozenSince],
  );

  const newCount = (recipes || []).filter(isNew).length;

  return { frozenSince, isNew, newCount };
};
